// StoryRow.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { color } from '../style/variables.js';
import debounce from 'lodash/debounce';

import HydrationBoundary from '../component/HydrationBoundary.jsx';
import WithIntersectionObserver from './WithIntersectionObserver.jsx';
import { InfiniteScroller, ScrollItem } from './InfiniteScroller.jsx';
import MessageCard from '../container/MessageCard.js';
import Spinning from '../style/Spinning.js';
import media from '../style/media.js';

import LoadingImage from '../../img/loading.png';
import MoreIconSrc from '../../img/ic_arrow_right_white.svg';

const isServer = typeof window === 'undefined';

const MORE_BUTTON_HEIGHT = 26;
const MESSAGE_CARD_WIDTH = 128;
const MESSAGE_CARD_HEIGHT = 172;
const MESSAGE_CARD_WIDTH_DESKTOP = 148;
const MESSAGE_CARD_HEIGHT_DESKTOP = 198;
const SHORTS_CARD_WIDTH = 160;
const SHORTS_CARD_HEIGHT = 240;
const SHORTS_CARD_WIDTH_DESKTOP = 212;
const SHORTS_CARD_HEIGHT_DESKTOP = 320;
const MESSAGE_CARD_GAP = 8;

export class StoryRow extends React.PureComponent {
  state = { isFirstFrame: true };
  nextTicks = [];
  messagesElement = undefined;
  handleMessagesScroll = debounce(() => {
    const { badgeName, setScrollLeft } = this.props;
    if (this.messagesElement) {
      setScrollLeft({
        scrollLeft: this.messagesElement.scrollLeft,
        category: badgeName,
      });
    }
  }, 500);
  componentDidMount() {
    const { isFirstPageFetched } = this.props;
    if (window.__IS_HYDRATED__ || !isFirstPageFetched) {
      // don't need to hydrate since SSR data is empty.
      // mostly for local dev env.
      window.__IS_HYDRATED__ = 'StoryRow';
    }
    this.nextTicks.push(
      setTimeout(() => this.setState({ isFirstFrame: false }))
    );
  }

  componentDidUpdate(prevProps) {
    const { isAuthed: prevIsAuthed } = prevProps;
    const { isAuthed, shouldRefresh, fetchFirstPage, scrollLeft } = this.props;
    const { isFirstFrame } = this.state;

    if (
      isAuthed !== prevIsAuthed ||
      (shouldRefresh && shouldRefresh !== prevProps.shouldRefresh)
    ) {
      this.nextTicks.push(
        setTimeout(() => {
          fetchFirstPage({
            shouldSkipLocalCache: shouldRefresh,
          });
        })
      );
    }

    if (
      !isFirstFrame &&
      this.messagesElement &&
      scrollLeft > this.messagesElement.scrollLeft
    ) {
      this.messagesElement.scrollLeft = scrollLeft;
    }
  }

  componentWillUnmount() {
    clearTimeout(this.intersectionTimer);
    this.nextTicks.forEach(clearTimeout);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'StoryRow';
    }
  }

  handleIntersection = ({ isIntersecting }, unobserve) => {
    const { isFirstPageFetched, fetchFirstPage } = this.props;
    clearTimeout(this.intersectionTimer);
    if (isIntersecting) {
      this.intersectionTimer = setTimeout(() => {
        unobserve();
        if (!isFirstPageFetched) {
          fetchFirstPage();
        }
      }, 100);
    }
  };

  renderPlaceholders() {
    const { deviceWidth, maxPlaceholdersAmount, isShorts } = this.props;

    const placeholderAmount = Math.min(
      Math.ceil(deviceWidth / (MESSAGE_CARD_WIDTH + MESSAGE_CARD_GAP)),
      maxPlaceholdersAmount
    );

    return Array(placeholderAmount)
      .fill(null)
      .map((element, index) => (
        <MessageCardWrapper key={index} isShorts={isShorts}>
          <MessageCard />
        </MessageCardWrapper>
      ));
  }

  renderHydrateStoryCard({ messageId, index, category, listPath, isShorts }) {
    // Deliberately using `index` as the component key for Shorts
    // since the messageId is not unique in Shorts.
    return (
      <WithIntersectionObserver
        key={isShorts ? index : messageId}
        threshold={0.1}
      >
        {({ isIntersecting }) => {
          return (
            <HydrationBoundary
              shouldHydrate={isIntersecting}
              wrapper={<MessageCardWrapper isShorts={isShorts} />}
            >
              <MessageCard
                id={messageId}
                categoryIndex={index}
                categoryId={category}
                listPath={listPath}
                shouldWrapLink
                shouldShowUser
                shouldTrackEvent
                shouldShowTooltip
                shouldUseLazyThumbnail={false}
                isShorts={isShorts}
              />
            </HydrationBoundary>
          );
        }}
      </WithIntersectionObserver>
    );
  }

  renderStoryCard({ messageId, index, category, listPath, isShorts }) {
    // Deliberately using `index` as the component key for Shorts
    // since the messageId is not unique in Shorts.
    return (
      <WithIntersectionObserver
        key={isShorts ? index : messageId}
        threshold={0.1}
      >
        {({ isIntersecting }) => {
          if (isIntersecting) {
            return (
              <MessageCardWrapper isShorts={isShorts}>
                <MessageCard
                  id={messageId}
                  categoryIndex={index}
                  categoryId={category}
                  listPath={listPath}
                  shouldWrapLink
                  shouldShowUser
                  shouldTrackEvent
                  shouldShowTooltip
                  isShorts={isShorts}
                />
              </MessageCardWrapper>
            );
          }

          return <MessageCardWrapper isShorts={isShorts} />;
        }}
      </WithIntersectionObserver>
    );
  }

  render() {
    const {
      titleI18nId,
      moreLink,
      nextPage,
      messageIds,
      isShorts,
      isNextPageFetching,
      fetchNextPage,
      category,
      listPath,
      t,
    } = this.props;
    const { isFirstFrame } = this.state;

    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    const shouldRenderData = !isFirstFrame || shouldHydrate;

    const renderStoryCard = shouldHydrate
      ? this.renderHydrateStoryCard
      : this.renderStoryCard;

    const isFirstPageFetching = isNextPageFetching && messageIds.length === 0;
    return (
      <StyledStoryRow isShorts={isShorts}>
        <Header>
          <HeaderLeft>
            <Title>{t(titleI18nId)}</Title>
          </HeaderLeft>
          <HeaderRight>
            <CategoryMoreButton to={moreLink} title={`SWAG ${t(titleI18nId)}`}>
              <MoreButtonText>{t('more')}</MoreButtonText>
              <MoreButtonIcon src={MoreIconSrc} alt={t('more')} />
            </CategoryMoreButton>
          </HeaderRight>
        </Header>
        <WithIntersectionObserver
          shouldKeepObserve={true}
          onChange={this.handleIntersection}
        >
          {() =>
            !shouldRenderData || isFirstPageFetching ? (
              <Messages>
                <MessageContainer>{this.renderPlaceholders()}</MessageContainer>
              </Messages>
            ) : (
              <Messages
                onScroll={this.handleMessagesScroll}
                ref={el => (this.messagesElement = el)}
              >
                <InfiniteScroller
                  axis="x"
                  useWindow={false}
                  pageStart={1}
                  hasMore={nextPage != null}
                  loadMore={fetchNextPage}
                  isFetching={isNextPageFetching}
                  threshold={100}
                >
                  <MessageContainer>
                    <ScrollItem
                      loader={
                        <MessageCardWrapper isShorts={isShorts}>
                          <Loader>
                            <Loading src={LoadingImage} title="loading" />
                          </Loader>
                        </MessageCardWrapper>
                      }
                    >
                      {messageIds.length
                        ? messageIds.map((messageId, index) =>
                            renderStoryCard({
                              messageId,
                              index,
                              category,
                              listPath,
                              isShorts,
                            })
                          )
                        : this.renderPlaceholders()}
                    </ScrollItem>
                  </MessageContainer>
                </InfiniteScroller>
              </Messages>
            )
          }
        </WithIntersectionObserver>
      </StyledStoryRow>
    );
  }
}

StoryRow.propTypes = {
  t: PropTypes.func.isRequired,
  badgeName: PropTypes.string.isRequired,
  titleI18nId: PropTypes.string.isRequired,
  moreLink: PropTypes.string.isRequired,
  nextPage: PropTypes.number,
  messageIds: PropTypes.array.isRequired,
  isShorts: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  isAuthed: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  category: PropTypes.string,
  fetchFirstPage: PropTypes.func,
  fetchNextPage: PropTypes.func,
  deviceWidth: PropTypes.number,
  maxPlaceholdersAmount: PropTypes.number,
  setScrollLeft: PropTypes.func,
  scrollLeft: PropTypes.number,
  listPath: PropTypes.arrayOf(PropTypes.string),
};

StoryRow.defaultProps = {
  category: '',
  messageIds: [],
  nextPage: null,
  isAuthed: false,
  shouldRefresh: false,
  isShorts: false,
  isFirstPageFetched: false,
  isNextPageFetching: false,
  fetchFirstPage: () => null,
  fetchNextPage: () => null,
  deviceWidth: 375,
  maxPlaceholdersAmount: 10,
  setScrollLeft: () => null,
  scrollLeft: 0,
  listPath: [],
};

const StyledStoryRow = styled.article`
  background-color: ${color.black};
  overflow: hidden;
  height: ${({ isShorts }) =>
    MORE_BUTTON_HEIGHT +
    (isShorts ? SHORTS_CARD_HEIGHT_DESKTOP : MESSAGE_CARD_HEIGHT_DESKTOP) +
    24}px;
  ${media.tablet`
    height: ${({ isShorts }) =>
      MORE_BUTTON_HEIGHT +
      (isShorts ? SHORTS_CARD_HEIGHT : MESSAGE_CARD_HEIGHT) +
      12}px;
  `}
`;

const Header = styled.header`
  padding: 0px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
`;

const HeaderRight = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 8px;
`;

const CategoryMoreButton = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${color.white};
  height: ${MORE_BUTTON_HEIGHT}px;
  padding: 0px 8px;
  border-radius: 4px;
  border: solid 1px ${color.white};
  white-space: nowrap;
  :hover {
    opacity: 0.7;
  }
`;

const MoreButtonText = styled.span`
  margin-right: 4px;
  font-size: 12px;
  line-height: 1;
`;

const MoreButtonIcon = styled.img`
  height: 16px;
  width: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const Messages = styled.div`
  padding: 12px 16px 40px;
  width: 100vw;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
`;

const MessageContainer = styled.div`
  position: relative;
  display: inline-flex;
  height: ${MESSAGE_CARD_HEIGHT}px;
`;

const Loader = styled.div`
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.05);
  overflow: hidden;
`;

const Loading = styled.img`
  height: auto;
  width: 50%;
  animation: 1.4s infinite ${Spinning};
`;

const MessageCardWrapper = styled.div`
  width: ${({ isShorts }) =>
    isShorts ? SHORTS_CARD_WIDTH_DESKTOP : MESSAGE_CARD_WIDTH_DESKTOP}px;
  height: ${({ isShorts }) =>
    isShorts ? SHORTS_CARD_HEIGHT_DESKTOP : MESSAGE_CARD_HEIGHT_DESKTOP}px;
  margin-right: 8px;
  :last-child {
    margin-right: 0;
  }
  ${media.tablet`
    width: ${({ isShorts }) =>
      isShorts ? SHORTS_CARD_WIDTH : MESSAGE_CARD_WIDTH}px;
    height: ${({ isShorts }) =>
      isShorts ? SHORTS_CARD_HEIGHT : MESSAGE_CARD_HEIGHT}px;
  `};
`;

export default withTranslation()(StoryRow);
